import { HealthMonitorSession } from "@binah/web-sdk";
import useFaceScanStore from "store/face-scan";
import { RgbCollector } from "./rgb-collector";

export interface RgbCollectionSession extends HealthMonitorSession {
  startRgbSignalCollection?(outputPath?: string): Promise<void>;
  stopRgbSignalCollection?(): Promise<string | null>;
  getRgbCollectionState?(): boolean;
}

const rgbCollectors = new WeakMap<HealthMonitorSession, RgbCollector>();

export function patchSessionForRgbCollection(
  session: HealthMonitorSession,
  videoElement: HTMLVideoElement
): RgbCollectionSession {
  const collector = new RgbCollector({ videoElement });
  rgbCollectors.set(session, collector);

  const originalStart = session.start.bind(session);
  session.start = function () {
    const col = rgbCollectors.get(session);
    if (col) col.startCollection();
    return originalStart();
  };

  const originalStop = session.stop.bind(session);
  session.stop = function () {
    const result = originalStop();
    const col = rgbCollectors.get(session);
    if (col && col.getCollectionState()) {
      const json = col.stopCollection();
      if (json) {
        try {
          const data = JSON.parse(json);
          console.info("RGB collection: Stopped", data);

          const presignedUrls = useFaceScanStore.getState().presignedUrls;
          const jsonUploadUrl = presignedUrls.json;

          if (jsonUploadUrl) {
            console.log("RGB collection: Uploading JSON to:", jsonUploadUrl);
            fetch(jsonUploadUrl, {
              method: "PUT",
              body: json,
              headers: {
                "Content-Type": "application/json",
              },
            })
              .then((response) => {
                if (response.ok) {
                  console.info("RGB collection: JSON upload successful");
                } else {
                  console.error(
                    "RGB collection: JSON upload failed",
                    response.statusText
                  );
                }
              })
              .catch((err) => {
                console.error("RGB collection: JSON upload error", err);
              });
          }
        } catch {
          console.info("RGB collection: Stopped, JSON length:", json.length);
        }
      }
    }
    return result;
  };

  (session as RgbCollectionSession).startRgbSignalCollection = async function (
    _outputPath?: string
  ): Promise<void> {
    const col = rgbCollectors.get(session);
    if (!col) throw new Error("RGB collector not initialized");
    col.startCollection();
  };

  (session as RgbCollectionSession).stopRgbSignalCollection =
    async function (): Promise<string | null> {
      const col = rgbCollectors.get(session);
      if (!col) return null;
      return col.stopCollection();
    };

  (session as RgbCollectionSession).getRgbCollectionState =
    function (): boolean {
      const col = rgbCollectors.get(session);
      return col ? col.getCollectionState() : false;
    };

  return session as RgbCollectionSession;
}
